import React, { FC } from 'react';
import { View, Text, ViewStyle } from 'react-native';
import { scale } from '@components/frame';
import i18n from 'i18n-js';
import { font } from '@styles/fonts';
import { formatSinceTimeWithDayTranslationCase, isPhone, localTime, machineStatus as machineStatusEnum } from '@helpers';
import { is24HourFormat } from '@components/frame';
import { BULLET, LINE_HORIZONTAL_PADDING, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';
import MachineStatusBadge from '@v2/pages/common/components/MachinesStatusBadge/MachineStatusBadge';
import ItemTime from '@components/machines/components/detailsComponents/activeMachine/components/TimeLine';
import { ProductivityStatusProps } from '@v2/pages/batch/details/modules/batch/interfaces';
import TimeDisplayInStaticFormat from '@components/common/TimeDisplayInStaticFormat';
import moment from 'moment';
import { LineWithTimelineAndTime } from './LineWithTimelineAndTime';
import { TimeLineWrapper } from './TimeLineWrapper';
import { styles } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/styles';

export const ProductivityStatus: FC<ProductivityStatusProps> = ({
  C,
  batch,
  onPress,
  offset,
  rowPaddings,
  rowStyles,
  leftColumnStyles,
  menuItemTextStyles,
  rightColumnStyles,
  machineStatus,
  borderRadiusConfig,
  withoutStretchingForContainer,
  separateTextAndIconForMobile,
  layoutForPhone,
  borderStyles,
  events,
  bg,
  setActualTimeForLatestEvent,
  testID,
}) => {
  const _rowPaddings = rowPaddings ?? styles.rowPaddings;
  const _rowStyles: ViewStyle = (rowStyles as ViewStyle) ?? {
    ...styles.rowStylesWithoutPaddings,
    ...styles.rowPaddings,
    ...(layoutForPhone ? { justifyContent: 'space-between', paddingRight: scale(15) } : {}),
  };

  const subStatusTextStyles = [font.Footnote, { color: C('Label/Light/Tertiary') }];

  const latestEvent = events?.length ? events[events.length - 1] : undefined;
  const latestEventStartedOn = latestEvent?.startedOn;
  const isNotProductive = machineStatus?.status === machineStatusEnum.NOTPRODUCTIVE;
  const startedAtContent = latestEventStartedOn ? (
    isNotProductive ? (
      <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>
        {formatSinceTimeWithDayTranslationCase(latestEventStartedOn, is24HourFormat)}
      </Text>
    ) : (
      <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>
        {i18n.t('Started at')}{' '}
        <Text style={[font['Time 3'], { color: C('Label/Light/Tertiary') }]}>{localTime(latestEventStartedOn).format('HH:mm')}</Text>
      </Text>
    )
  ) : null;

  return (
    <LineWithTimelineAndTime
      {...{
        testID,
        bg,
        layoutForPhone,
        withoutStretchingForContainer,
        productionBatchId: batch?.productionBatchId,
        onPress,
        borderStyles: borderStyles ?? { borderBottomColor: 'transparent' },
        rowPaddings: {
          ..._rowPaddings,
          paddingBottom: scale(9.5),
        },
        rowStyles: _rowStyles,
        leftColumnStyles,
        menuItemTextStyles,
        rightColumnStyles,
        borderRadiusConfig: borderRadiusConfig ?? {
          borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
          borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
        },
        name: 'Productivity Status',
        timeOffset: offset,
        C,
        TimeLineComponent: (
          <ItemTime
            setActualTimeForLatestEvent={setActualTimeForLatestEvent}
            targetMarkTop={-2}
            targetMarkHeight={10}
            timelineHeight={6}
            timelineBorderRadius={6}
            layoutForPhone={layoutForPhone}
            timeOffset={offset}
            C={C}
            machineStatus={machineStatus}
            hideNCFile
            hideStatusName
            hideDurationTime={!batch?.links?.length}
            selected={false}
            timeLineStyle={{ paddingRight: scale(LINE_HORIZONTAL_PADDING) }}
            timeMarginTop={10.5}
            getTimeLineWrapper={(children) => (
              <TimeLineWrapper
                {...{
                  leftColumnStyles,
                  rowStyles: { ..._rowStyles, paddingLeft: 0 },
                  rightColumnStyles,
                  leftContent: startedAtContent,
                }}
              >
                {children}
              </TimeLineWrapper>
            )}
            getTimeDisplayComponent={(timeValue) => <TimeDisplayInStaticFormat {...{ timeValue, C }} />}
          />
        ),
        StatusElement: (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5) }}>
            <MachineStatusBadge separateTextAndIconForMobile={separateTextAndIconForMobile || isPhone} />
            {!isPhone && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(5) }}>
                {machineStatus?.executionMode && (
                  <>
                    <Text style={subStatusTextStyles}>{BULLET}</Text>
                    <Text style={subStatusTextStyles}>{i18n.t(machineStatus?.executionMode)}</Text>
                  </>
                )}
                <Text style={subStatusTextStyles}>{BULLET}</Text>
                <Text style={subStatusTextStyles}>{i18n.t(machineStatus?.subStatus)}</Text>
              </View>
            )}
          </View>
        ),
        timeValue: moment(latestEvent?.endedOn).diff(moment(latestEvent?.startedOn), 'seconds'),
        startedOn: latestEvent?.startedOn,
        useSinceLabel: machineStatus?.status === machineStatusEnum.NOTPRODUCTIVE,
      }}
    />
  );
};
